import { button, element, heading, type ElementChild } from "@groundspark/browser-ui";
import { remaining } from "./domain";
export { button, element, heading };

export interface View {
  readonly element: HTMLElement;
  dispose(): void | Promise<void>;
}
export function message(failure: unknown): string {
  return failure instanceof Error ? failure.message : String(failure);
}
export function errorMessage(): HTMLParagraphElement {
  const result = element("p", "error");
  result.setAttribute("role", "alert");
  return result;
}
export function notice(...children: ElementChild[]): HTMLParagraphElement {
  const result = element("p", "muted", ...children);
  result.setAttribute("role", "status");
  return result;
}
export function panel(...children: ElementChild[]): HTMLElement {
  return element("section", "panel", ...children);
}
export function actions(...children: ElementChild[]): HTMLElement {
  return element("div", "actions", ...children);
}
export function field(
  label: string,
  name: string,
  type = "text",
  autocomplete = "off",
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const input = element("input");
  Object.assign(input, { name, type, autocomplete, required: true });
  return { label: element("label", "field", label, input), input };
}
export function selectField(
  label: string,
  options: readonly (readonly [string, string])[],
  selected?: string,
): { label: HTMLLabelElement; select: HTMLSelectElement } {
  const select = element("select");
  for (const [value, label] of options) {
    const option = element("option", null, label);
    option.value = value;
    select.append(option);
  }
  if (selected !== undefined) select.value = selected;
  return { label: element("label", "field", label, select), select };
}
export function runButton(
  label: string,
  action: () => Promise<void>,
  error: HTMLElement,
  className = "button",
): HTMLButtonElement {
  const result = button(label, className, async () => {
    if (result.disabled) return;
    result.disabled = true;
    error.textContent = "";
    try {
      await action();
    } catch (failure) {
      error.textContent = message(failure);
    } finally {
      result.disabled = false;
    }
  });
  return result;
}
export function countdown(expiresAt: string): HTMLElement {
  const result = element("span", "countdown");
  result.dataset.expires = expiresAt;
  result.textContent = remaining(expiresAt);
  return result;
}
export async function confirmAction(
  title: string,
  explanation: string,
  confirmLabel: string,
): Promise<boolean> {
  const dialog = element("dialog", "modal");
  const confirm = button(confirmLabel, "button button--danger", () =>
    dialog.close("confirmed"),
  );
  const cancel = button("Cancel", "button button--secondary", () => dialog.close());
  dialog.append(heading(2, title), element("p", null, explanation), actions(cancel, confirm));
  document.body.append(dialog);
  return new Promise((resolve) => {
    dialog.addEventListener(
      "close",
      () => {
        const confirmed = dialog.returnValue === "confirmed";
        dialog.remove();
        resolve(confirmed);
      },
      { once: true },
    );
    dialog.showModal();
    cancel.focus();
  });
}

export class Lifetime {
  public readonly abort = new AbortController();
  private readonly cleanups: (() => void)[] = [];
  public get active(): boolean {
    return !this.abort.signal.aborted;
  }
  public add(cleanup: () => void): void {
    this.cleanups.push(cleanup);
  }
  public dispose(): void {
    this.abort.abort();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
  }
}
